<template>
  <div>
    <alert
      v-if='alertVisible'
      :message='alertMessage'
      :type='alertType'>
    </alert>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'Settings'})"/>
                {{ $t('health.assignPatients.title') }}
              </h1>
            </div>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="patientTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                label=""
                header-class="text-left"
                cell-class="table-component__text-left">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="displayModal(row)"
                    :icon="icon.plus"
                    class="table-iconset-icon red pointer"/>
                </template>
              </table-column>
              <table-column
                label=""
                header-class="text-left"
                cell-class="table-component__text-left">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.viber_plus"
                    class="rotated-90 red zoom"
                    :icon="icon.viberPlusLogo"
                  />
                  <font-awesome-icon
                    v-else-if="row.viber"
                    class="rotated-90 red zoom"
                    :icon="icon.viberLogo"
                  />
                </template>
              </table-column>
              <table-column
                show="id"
                :hidden="true"
                :filterable="false"
                data-type="boolean">
              </table-column>
              <table-column
                show="username"
                :label="$t('health.patient.username')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="name"
                :label="$t('health.patient.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="surnames"
                :label="$t('health.patient.surname')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="email"
                :label="$t('health.patient.email')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

            </table-component>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
    <assign-nutri-modal
      v-if="showModal"
      v-model="selectedNutri"
      :nutritionists="getNutritionists"
      v-on:close="closeModal"
      v-on:input="addNutri"
    />
  </div>

</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import Chevron from '@/components/shared/Chevron';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import patientApi from 'BRAND/api/patient';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { faHexagon, faPlusHexagon } from '@fortawesome/pro-regular-svg-icons';
import AssignNutriModal from 'BRAND/components/patients/AssignNutriModal';
import Alert from '@/components/shared/alert/Alert';

export default {
  name: 'AssignPatients',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    Chevron,
    AssignNutriModal,
    Alert
  },
  data () {
    return {
      unassignedPatients: [],
      nutritionists: [],
      selectedNutri: undefined,
      selectedUser: undefined,
      showModal: false,
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      icon: {
        viberLogo: faHexagon,
        viberPlusLogo: faPlusHexagon,
        plus: faPlusCircle
      },
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  mounted () {
    this.requestData();
  },
  watch: {
    getUnassignedPatients: {
      handler: function () {
        this.$refs.patientTable.refresh();
      }
    }
  },
  computed: {
    getBreadcrumb () {
      return [
        {name: this.$t('menu.settings'), link: '/settings'},
        {name: this.$t('health.assignPatients.title'), disabled: true}
      ];
    },
    getUnassignedPatients () {
      return this.unassignedPatients;
    },
    getNutritionists () {
      return this.nutritionists;
    }
  },
  methods: {
    requestData () {
      patientApi.getUnassignedPatients(this).then((data) => {
        this.unassignedPatients = data;
      });
      patientApi.getNutritionistsAndPrescriptors(this).then((data) => {
        this.nutritionists = data;
      });
    },
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getUnassignedPatients,
        this.tableState,
        ['username', 'name', 'surname', 'email'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    displayModal (row) {
      this.selectedUser = row;
      this.showModal = true;
    },
    addNutri (nutri) {
      patientApi.assignNutritionist(this, this.selectedUser.id, nutri)
        .then(() => {
          showAlert(this, this.$t('health.assignPatients.successAssignNutritionist'), 'success');
          this.requestData();
        })
        .catch((error) => showAlert(this, error.data.message, 'error'));
    },
    closeModal () {
      this.showModal = false;
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.red {
  color: $primary;
}
.rotated-90 {
  transform: rotate(90deg);
}

.zoom:hover {
  transform: rotate(360deg);
  transition: 2s ease-out;
}

.pointer:hover {
  cursor: pointer;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/patient/AssignPatients.vue